import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	defaultPage,
	diagnosticsClient,
	formatBytes,
	shortId,
} from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	Activity,
	CheckCircle2,
	Circle,
	HardDrive,
	Monitor,
	Radio,
	Search,
	Shield,
	Wifi,
	WifiOff,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export const Route = createFileRoute("/diagnostics/")({
	component: DiagnosticsDashboardPage,
	validateSearch: (search: Record<string, unknown>) => ({
		q: typeof search.q === "string" ? search.q : undefined,
		employee: typeof search.employee === "string" ? search.employee : undefined,
	}),
});

type SearchMode = "timeline" | "hybrid" | "natural";

const tooltipStyle = {
	borderRadius: "6px",
	border: "1px solid hsl(var(--border))",
	backgroundColor: "hsl(var(--popover))",
	color: "hsl(var(--popover-foreground))",
	fontSize: 11,
	padding: "6px 8px",
};

function formatHour(ts: number): string {
	const d = new Date(ts * 1000);
	return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number): string {
	if (ms < 60_000) return `${Math.round(ms / 1000)}с`;
	if (ms < 3_600_000) return `${Math.round(ms / 60_000)}м`;
	const h = Math.floor(ms / 3_600_000);
	const m = Math.round((ms % 3_600_000) / 60_000);
	return `${h}ч ${m}м`;
}

function relativeTime(
	ts: { seconds: bigint | number } | null | undefined,
): string {
	if (!ts) return "\u2014";
	const now = Date.now();
	const then = Number(ts.seconds) * 1000;
	const diff = now - then;
	if (diff < 0) return "\u2014";
	if (diff < 60_000) return "только что";
	if (diff < 3_600_000) return `${Math.round(diff / 60_000)} мин назад`;
	if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} ч назад`;
	return `${Math.round(diff / 86_400_000)} д назад`;
}

function DiagnosticsDashboardPage() {
	const { t } = useTranslation("diagnostics");
	const search = Route.useSearch();
	useBreadcrumbs([{ label: t("title"), href: "/diagnostics" }]);

	// ── Data queries ───────────────────────────────────────────────
	const healthQuery = useQuery({
		queryKey: ["diagnostics", "health"],
		queryFn: () => diagnosticsClient.getDiagnosticsHealth({}),
		refetchInterval: 10_000,
	});

	const storageQuery = useQuery({
		queryKey: ["diagnostics", "storage-count"],
		queryFn: () => diagnosticsClient.listStorageTargets({}),
	});

	const policiesQuery = useQuery({
		queryKey: ["diagnostics", "policies-count"],
		queryFn: () => diagnosticsClient.listRecordingPolicies({}),
	});

	const devicesQuery = useQuery({
		queryKey: ["diagnostics", "devices-count"],
		queryFn: () => diagnosticsClient.listEnrollmentTokens({}),
	});

	const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

	const timelineQuery = useQuery({
		queryKey: ["diagnostics", "activity-timeline", today, 1],
		queryFn: () =>
			diagnosticsClient.getActivityTimeline({
				date: today,
				employeeId: "",
				days: 1,
			}),
	});

	const employeesQuery = useQuery({
		queryKey: ["diagnostics", "live-employees"],
		queryFn: () => diagnosticsClient.getLiveEmployees({}),
		refetchInterval: 10_000,
	});

	const health = healthQuery.data?.stats;
	const storageCount = storageQuery.data?.targets?.length ?? 0;
	const policyCount = policiesQuery.data?.policies?.length ?? 0;
	const deviceCount = devicesQuery.data?.tokens?.length ?? 0;
	const sessionCount =
		Number(health?.activeSessions ?? 0) +
		Number(health?.sessionsCompleted ?? 0);
	const employees = employeesQuery.data?.employees ?? [];

	const timelineData = useMemo(() => {
		return (timelineQuery.data?.entries ?? []).map((e) => ({
			hour: e.hour ? Number(e.hour.seconds) : 0,
			duration: Number(e.durationMs),
			segments: Number(e.segmentCount),
			apps: e.uniqueApps,
		}));
	}, [timelineQuery.data]);

	const allZero =
		!healthQuery.isLoading &&
		Number(health?.activeSessions ?? 0) === 0 &&
		Number(health?.totalSegments ?? 0) === 0 &&
		Number(health?.totalBytesUploaded ?? 0) === 0;

	const showChecklist =
		allZero || storageCount === 0 || policyCount === 0 || deviceCount === 0;

	// ── Search state ───────────────────────────────────────────────
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [searchText, setSearchText] = useState(search.q ?? "");
	const [searchMode, setSearchMode] = useState<SearchMode>("timeline");
	const [submittedSearch, setSubmittedSearch] = useState(search.q ?? "");
	const [showResults, setShowResults] = useState(!!search.q);

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "/" && !e.ctrlKey && !e.metaKey) {
				const tag = (e.target as HTMLElement).tagName;
				if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
				e.preventDefault();
				searchInputRef.current?.focus();
			}
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, []);

	const submitSearch = useCallback(() => {
		if (searchText.trim().length === 0) return;
		setSubmittedSearch(searchText.trim());
		setShowResults(true);
	}, [searchText]);

	const timelineSearchQuery = useQuery({
		queryKey: ["diagnostics", "dashboard-search", "timeline", submittedSearch],
		queryFn: () =>
			diagnosticsClient.searchTimelineSegments({
				employeeId: "",
				recordingSessionId: "",
				appName: "",
				searchText: submittedSearch,
				fromTime: undefined,
				toTime: undefined,
				page: defaultPage(20),
			}),
		enabled:
			showResults && searchMode === "timeline" && submittedSearch.length > 0,
	});

	const hybridSearchQuery = useQuery({
		queryKey: ["diagnostics", "dashboard-search", "hybrid", submittedSearch],
		queryFn: () =>
			diagnosticsClient.hybridSearch({
				query: submittedSearch,
				employeeId: "",
				appName: "",
				fromTime: undefined,
				toTime: undefined,
				limit: 20,
				page: defaultPage(20),
			}),
		enabled:
			showResults && searchMode === "hybrid" && submittedSearch.length > 0,
	});

	const nlSearchQuery = useQuery({
		queryKey: ["diagnostics", "dashboard-search", "natural", submittedSearch],
		queryFn: () =>
			diagnosticsClient.naturalLanguageSearch({
				query: submittedSearch,
				limit: 20,
			}),
		enabled:
			showResults && searchMode === "natural" && submittedSearch.length > 0,
	});

	const searchResults = useMemo(() => {
		if (!showResults) return [];
		if (searchMode === "timeline") {
			return (timelineSearchQuery.data?.segments ?? []).map((s) => ({
				id: s.recordingArtifactId,
				app:
					s.focusedAppName ||
					t("search_unknown_app", { defaultValue: "Неизвестно" }),
				title: s.focusedWindowTitle || "",
				employeeId: s.employeeId,
				score: 0,
			}));
		}
		if (searchMode === "hybrid") {
			return (hybridSearchQuery.data?.results ?? []).map((r) => ({
				id: r.segment?.recordingArtifactId ?? "",
				app:
					r.segment?.focusedAppName ||
					t("search_unknown_app", { defaultValue: "Неизвестно" }),
				title: r.matchedText || r.segment?.focusedWindowTitle || "",
				employeeId: r.segment?.employeeId ?? "",
				score: r.relevanceScore,
			}));
		}
		return (nlSearchQuery.data?.results ?? []).map((r) => ({
			id: r.segment?.recordingArtifactId ?? "",
			app:
				r.segment?.focusedAppName ||
				t("search_unknown_app", { defaultValue: "Неизвестно" }),
			title: r.matchedText || r.segment?.focusedWindowTitle || "",
			employeeId: r.segment?.employeeId ?? "",
			score: r.relevanceScore,
		}));
	}, [
		showResults,
		searchMode,
		timelineSearchQuery.data,
		hybridSearchQuery.data,
		nlSearchQuery.data,
		t,
	]);

	const isSearching =
		timelineSearchQuery.isLoading ||
		hybridSearchQuery.isLoading ||
		nlSearchQuery.isLoading;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "active",
				content: t("live_status_active", {
					num: Number(health?.activeSessions ?? 0),
				}),
				align: "left" as const,
			},
		],
		[t, health?.activeSessions],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	// ── Helpers for checklist ──────────────────────────────────────
	const steps = [
		{
			done: storageCount > 0,
			label: t("setup_step_storage", { defaultValue: "Создайте хранилище" }),
			link: "/diagnostics/storage?create=1",
			icon: <HardDrive className="h-4 w-4" />,
		},
		{
			done: policyCount > 0,
			label: t("setup_step_policy", {
				defaultValue: "Настройте политику записи",
			}),
			link: "/diagnostics/policies?create=1",
			icon: <Shield className="h-4 w-4" />,
		},
		{
			done: deviceCount > 0,
			label: t("setup_step_device", { defaultValue: "Подключите устройство" }),
			link: "/diagnostics/devices?create=1",
			icon: <Monitor className="h-4 w-4" />,
		},
		{
			done: sessionCount > 0,
			label: t("setup_step_monitor", { defaultValue: "Начните мониторинг" }),
			link: "/diagnostics/live",
			icon: <Activity className="h-4 w-4" />,
		},
	];

	// ── Employee activity helpers ──────────────────────────────────
	const activityIcon = (activity: string) => {
		switch (activity) {
			case "recording":
				return <Radio className="h-3 w-3 text-red-500 animate-pulse" />;
			case "active":
				return <Wifi className="h-3 w-3 text-green-500" />;
			default:
				return <WifiOff className="h-3 w-3 text-muted-foreground" />;
		}
	};

	const activityLabel = (activity: string) => {
		switch (activity) {
			case "recording":
				return t("live_activity_recording", { defaultValue: "Запись" });
			case "active":
				return t("live_activity_active", { defaultValue: "Активен" });
			default:
				return t("live_activity_offline", { defaultValue: "Офлайн" });
		}
	};

	return (
		<div className="h-full overflow-y-auto">
			<div className="p-4 space-y-4">
				{/* ── Setup Checklist ────────────────────────────────── */}
				{showChecklist && (
					<Card>
						<CardHeader className="pb-3">
							<CardTitle className="text-sm">
								{t("setup_title", { defaultValue: "Настройка диагностики" })}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							{steps.map((step, i) => (
								<Link
									key={step.link}
									to={step.link}
									className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/50 transition-colors"
								>
									<span className="shrink-0">
										{step.done ? (
											<CheckCircle2 className="h-5 w-5 text-green-500" />
										) : (
											<Circle className="h-5 w-5 text-muted-foreground" />
										)}
									</span>
									<span className="shrink-0 text-muted-foreground">
										{step.icon}
									</span>
									<span className="text-sm">
										<span className="text-muted-foreground mr-1.5">
											{t("setup_step_prefix", { step: i + 1 })}
										</span>
										{step.label}
									</span>
								</Link>
							))}
						</CardContent>
					</Card>
				)}

				{/* ── Health KPI Strip ───────────────────────────────── */}
				{healthQuery.isLoading ? (
					<div className="grid grid-cols-4 gap-3">
						{Array.from({ length: 4 }).map((_, i) => (
							<Card key={`kpi-sk-${i}`} className="p-3">
								<Skeleton className="h-3 w-20 mb-1.5" />
								<Skeleton className="h-6 w-16" />
							</Card>
						))}
					</div>
				) : health ? (
					<div className="grid grid-cols-4 gap-3">
						<Card className="p-3">
							<p className="text-[10px] text-muted-foreground">
								{t("dashboard_kpi_active", { defaultValue: "Активные сессии" })}
							</p>
							<p className="text-lg font-semibold tabular-nums">
								{Number(health.activeSessions)}
							</p>
						</Card>
						<Card className="p-3">
							<p className="text-[10px] text-muted-foreground">
								{t("dashboard_kpi_segments", {
									defaultValue: "Всего сегментов",
								})}
							</p>
							<p className="text-lg font-semibold tabular-nums">
								{Number(health.totalSegments).toLocaleString()}
							</p>
						</Card>
						<Card className="p-3">
							<p className="text-[10px] text-muted-foreground">
								{t("dashboard_kpi_bytes", { defaultValue: "Загружено данных" })}
							</p>
							<p className="text-lg font-semibold tabular-nums">
								{formatBytes(Number(health.totalBytesUploaded))}
							</p>
						</Card>
						<Card className="p-3">
							<p className="text-[10px] text-muted-foreground">
								{t("dashboard_kpi_heartbeat", {
									defaultValue: "Последний сигнал",
								})}
							</p>
							<p className="text-lg font-semibold tabular-nums">
								{relativeTime(health.lastHeartbeatAt)}
							</p>
						</Card>
					</div>
				) : null}

				{/* ── Quick Search Bar ───────────────────────────────── */}
				<form
					className="flex items-center gap-2"
					onSubmit={(e) => {
						e.preventDefault();
						submitSearch();
					}}
				>
					<div className="relative flex-1 max-w-xl">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							ref={searchInputRef}
							className="h-9 pl-9 text-sm"
							placeholder={t("dashboard_search_placeholder", {
								defaultValue: "Поиск по записям... (/ для фокуса)",
							})}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
						/>
					</div>
					<div className="flex items-center gap-1">
						{(["timeline", "hybrid", "natural"] as SearchMode[]).map((m) => {
							const labels: Record<SearchMode, string> = {
								timeline: t("search_mode_timeline", {
									defaultValue: "Таймлайн",
								}),
								hybrid: t("search_mode_hybrid", { defaultValue: "Гибридный" }),
								natural: t("search_mode_natural", { defaultValue: "ИИ" }),
							};
							return (
								<Button
									key={m}
									type="button"
									size="sm"
									variant={searchMode === m ? "default" : "outline"}
									className="h-9 text-xs"
									onClick={() => setSearchMode(m)}
								>
									{labels[m]}
								</Button>
							);
						})}
					</div>
					<Button type="submit" size="sm" className="h-9">
						{t("search_action", { defaultValue: "Найти" })}
					</Button>
					{showResults && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-9"
							onClick={() => {
								setShowResults(false);
								setSubmittedSearch("");
								setSearchText("");
							}}
						>
							{t("search_clear", { defaultValue: "Сбросить" })}
						</Button>
					)}
				</form>

				{/* ── Search Results (inline) ────────────────────────── */}
				{showResults && (
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm">
								{t("dashboard_search_results", {
									defaultValue: "Результаты поиска",
								})}
								{searchResults.length > 0 && (
									<Badge variant="secondary" className="ml-2 text-[10px]">
										{searchResults.length}
									</Badge>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{isSearching ? (
								<div className="space-y-2">
									<Skeleton className="h-10 w-full" />
									<Skeleton className="h-10 w-full" />
									<Skeleton className="h-10 w-full" />
								</div>
							) : searchResults.length === 0 ? (
								<p className="text-xs text-muted-foreground py-4 text-center">
									{t("search_empty", { defaultValue: "Ничего не найдено" })}
								</p>
							) : (
								<div className="divide-y">
									{searchResults.map((r) => (
										<div
											key={r.id}
											className="flex items-center justify-between py-2"
										>
											<div className="min-w-0 space-y-0.5">
												<div className="flex items-center gap-2">
													<span className="text-xs font-medium">{r.app}</span>
													{r.score > 0 && (
														<Badge
															variant="secondary"
															className="text-[10px] h-4 px-1"
														>
															{Math.round(r.score * 100)}%
														</Badge>
													)}
												</div>
												{r.title && (
													<p className="text-[10px] text-muted-foreground truncate max-w-md">
														{r.title}
													</p>
												)}
											</div>
											<span className="text-[10px] text-muted-foreground shrink-0">
												{shortId(r.employeeId)}
											</span>
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				)}

				{/* ── Two Column: Timeline + Live Employees ─────────── */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
					{/* Left (2/3): Activity Timeline */}
					<Card className="lg:col-span-2">
						<CardHeader className="pb-2">
							<CardTitle className="text-sm">
								{t("dashboard_activity_timeline", {
									defaultValue: "Активность за сегодня",
								})}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{timelineQuery.isLoading ? (
								<Skeleton className="h-[220px] w-full" />
							) : timelineData.length === 0 ? (
								<div className="h-[220px] flex items-center justify-center text-xs text-muted-foreground">
									{t("analytics_no_data", { defaultValue: "Нет данных" })}
								</div>
							) : (
								<ResponsiveContainer width="100%" height={220}>
									<BarChart data={timelineData}>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke="hsl(var(--border))"
										/>
										<XAxis
											dataKey="hour"
											tickFormatter={formatHour}
											tick={{ fontSize: 10 }}
										/>
										<YAxis
											tickFormatter={(v) => formatDuration(v)}
											tick={{ fontSize: 10 }}
										/>
										<Tooltip
											contentStyle={tooltipStyle}
											labelFormatter={(v) => formatHour(Number(v))}
											formatter={(v, name) => [
												name === "duration"
													? formatDuration(Number(v))
													: String(v),
												name === "duration"
													? t("analytics_duration", {
															defaultValue: "Длительность",
														})
													: t("analytics_segments", {
															defaultValue: "Сегменты",
														}),
											]}
										/>
										<Bar
											dataKey="duration"
											fill="hsl(var(--primary))"
											radius={[3, 3, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							)}
						</CardContent>
					</Card>

					{/* Right (1/3): Live Employees */}
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm flex items-center justify-between">
								<span>
									{t("dashboard_live_employees", {
										defaultValue: "Сотрудники онлайн",
									})}
								</span>
								{employees.length > 0 && (
									<Badge variant="secondary" className="text-[10px]">
										{employees.length}
									</Badge>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{employeesQuery.isLoading ? (
								<div className="space-y-2">
									<Skeleton className="h-10 w-full" />
									<Skeleton className="h-10 w-full" />
									<Skeleton className="h-10 w-full" />
								</div>
							) : employees.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-8">
									<Monitor className="h-8 w-8 mb-2 opacity-40" />
									<p className="text-xs text-muted-foreground">
										{t("live_no_employees", {
											defaultValue: "Нет активных сотрудников",
										})}
									</p>
								</div>
							) : (
								<div className="space-y-1.5 max-h-[240px] overflow-y-auto">
									{employees.map((emp) => (
										<Button
											key={`${emp.employeeId}-${emp.deviceId}`}
											variant="ghost"
											className="flex w-full items-center justify-between h-auto px-2 py-1.5"
											onClick={() => {
												setSearchText(`employee:${shortId(emp.employeeId)}`);
												setSubmittedSearch(
													`employee:${shortId(emp.employeeId)}`,
												);
												setShowResults(true);
											}}
										>
											<div className="flex items-center gap-2 min-w-0">
												{activityIcon(emp.activity)}
												<span className="text-xs font-medium truncate">
													{shortId(emp.employeeId)}
												</span>
											</div>
											<div className="flex items-center gap-2 shrink-0">
												{emp.captureProfile && (
													<span className="text-[10px] text-muted-foreground">
														{emp.captureProfile}
													</span>
												)}
												<Badge
													variant="secondary"
													className="text-[9px] h-4 px-1"
												>
													{activityLabel(emp.activity)}
												</Badge>
											</div>
										</Button>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
